/**
 * SONIC FIDELITY ENGINE
 * Based on Research Section: "Temporal Immediacy (Sonic Feedback)"
 *
 * Uses the Web Audio API to bypass the DOM event loop latency.
 * Generates a synthetic "Transient" (click) characteristic of Apple's UI sounds.
 *
 * Key insight from research: "Haptics must be treated as a design layer
 * equivalent to audio" - standard HTML5 audio has 50-200ms latency which
 * creates "sensory disconnect". Web Audio API achieves sub-20ms response.
 */

// Singleton context to prevent garbage collection hiccups
let audioCtx: AudioContext | null = null;

// Feature detection type for webkit prefix
interface WebkitWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

/**
 * Initialize the audio context lazily (required for autoplay policies)
 * Must be called after user interaction on iOS/Safari
 */
const initAudio = (): void => {
  if (typeof window !== 'undefined' && !audioCtx) {
    // Cross-browser support with webkit prefix
    const AudioContextClass = window.AudioContext || (window as WebkitWindow).webkitAudioContext;

    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
};

/**
 * Resume audio context if suspended (required after user gesture on iOS)
 */
const ensureAudioReady = async (): Promise<void> => {
  if (!audioCtx) initAudio();
  if (audioCtx?.state === 'suspended') {
    await audioCtx.resume();
  }
};

export type UISoundType = 'success' | 'neutral' | 'error' | 'tick';

/**
 * Trigger a synthesized UI sound with zero latency
 *
 * Sound profiles modeled after iOS system sounds:
 * - neutral: Subtle "thock" for button presses (sine wave, fast decay)
 * - success: High-pitch "ping" for confirmations (triangle wave)
 * - error: Low "bonk" for errors (sawtooth wave)
 * - tick: Ultra-short click for scroll/swipe feedback
 */
export const triggerUISound = (type: UISoundType = 'neutral'): void => {
  // Initialize on first call
  if (!audioCtx) initAudio();
  if (!audioCtx) return;

  // Don't block on suspended context - just skip for smoother UX
  if (audioCtx.state === 'suspended') {
    ensureAudioReady();
    return;
  }

  // 1. Create Oscillator (The physical material)
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  const now = audioCtx.currentTime;

  // 2. Sculpt the Sound (The Physics)
  // iOS sounds are extremely short (< 0.05s) transients
  switch (type) {
    case 'neutral':
      // A subtle "Thock" (Sine wave with fast exponential decay)
      // Mimics the iOS keyboard click
      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(100, now + 0.05);

      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

      osc.start(now);
      osc.stop(now + 0.05);
      break;

    case 'success':
      // High-pitch "Ping" - ascending frequency
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.linearRampToValueAtTime(1200, now + 0.05);

      gain.gain.setValueAtTime(0.1, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.1);

      osc.start(now);
      osc.stop(now + 0.1);
      break;

    case 'error':
      // Low "Bonk" - descending, slightly harsh
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(80, now + 0.08);

      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

      osc.start(now);
      osc.stop(now + 0.1);
      break;

    case 'tick':
      // Ultra-short tick for scroll/swipe feedback
      // Almost imperceptible, but adds "weight"
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);

      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.015);

      osc.start(now);
      osc.stop(now + 0.02);
      break;
  }
};

/**
 * Pre-warm the audio context (call after first user interaction)
 * This prevents the first sound from having extra latency
 */
export const prewarmAudio = (): void => {
  ensureAudioReady();
};
